import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type FocusEvent,
  type KeyboardEvent,
} from 'react';
import { Pencil, Plus } from 'lucide-react';

import { ControlBar } from './ControlBar';
import { formatPlaybackTime } from './TimeDisplay';
import { VideoSelector } from './VideoSelector';
import { YouTubePlayer } from './YouTubePlayer';
import { useConfusedSentiment } from './useConfusedSentiment';
import { useSentimentData } from './useSentimentData';
import type { PlaybackRate, VideoPlayerHandle } from './player.types';
import { PLAYER_STATES } from './player.types';
import type { UserRole } from '@/features/courses/useCourse';
import type { RawComponent } from '../useComponent';
import {
  useOptionalActiveEdit,
  useOptionalPageEditMode,
} from '../editor/ContentEditModeProvider';
import { VideoSectionEditor } from '../editor/VideoSectionEditor';
import type { VideoSectionStatsMap } from './sentimentTypes';
import styles from './VideoSection.module.css';

interface VideoSectionProps {
  canEdit?: boolean;
  className?: string;
  componentId: string;
  courseId: string;
  rawComponent: RawComponent;
  onBeforeEdit?: () => void;
  userRole: UserRole;
  videoIds: string[];
  videoSectionMap: Record<string, string>;
  videoSectionStats: VideoSectionStatsMap;
}

// Until the player is ready, Play, Mute, speed and volume can reach nothing,
// and say why (issue #121).
const PLAYER_LOADING_REASON = 'The video is still loading.';

function clampPlaybackRate(value: number): PlaybackRate {
  if (value === 0.5 || value === 0.75 || value === 1.25 || value === 1.5 || value === 2) {
    return value;
  }

  return 1;
}

function getActiveVideoIndex(videoIds: string[], activeVideoId: string | null) {
  const index = activeVideoId ? videoIds.indexOf(activeVideoId) : -1;
  return index >= 0 ? index : 0;
}

function isFocusableControlsTarget(target: EventTarget | null) {
  return (
    target instanceof HTMLElement &&
    ['BUTTON', 'INPUT', 'OPTION', 'SELECT', 'TEXTAREA'].includes(target.tagName)
  );
}

export function VideoSection({
  canEdit = false,
  className,
  componentId,
  courseId,
  onBeforeEdit,
  rawComponent,
  userRole,
  videoIds,
  videoSectionMap,
  videoSectionStats,
}: VideoSectionProps) {
  const activeEdit = useOptionalActiveEdit();
  const pageEditMode = useOptionalPageEditMode();
  const sectionRef = useRef<HTMLElement | null>(null);
  const fullscreenTriggerRef = useRef<HTMLElement | null>(null);
  const lastSubmittedSentimentTimeRef = useRef<number | null>(null);
  const [activeVideoId, setActiveVideoId] = useState<string | null>(videoIds[0] ?? null);
  const [announcement, setAnnouncement] = useState('');
  const [controller, setController] = useState<VideoPlayerHandle | null>(null);
  const [controlsVisible, setControlsVisible] = useState(true);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [playbackRate, setPlaybackRate] = useState<PlaybackRate>(1);
  const [playerError, setPlayerError] = useState<string | null>(null);
  const [playerState, setPlayerState] = useState<number>(PLAYER_STATES.UNSTARTED);
  const [volume, setVolume] = useState(100);
  const sentiment = useConfusedSentiment();

  const activeVideoIndex = getActiveVideoIndex(videoIds, activeVideoId);
  const activeVideoSectionId = activeVideoId ? (videoSectionMap[activeVideoId] ?? null) : null;
  const isPlaying = playerState === PLAYER_STATES.PLAYING;
  const hasStartedPlayback = currentTime > 0 || isPlaying;
  const isStudentView = userRole === 'student';
  const isInstructorView = userRole === 'instructor';
  const hasVideos = videoIds.length > 0;
  const isVideoEditTarget =
    Boolean(activeEdit?.videoEditTargetId) &&
    activeEdit?.activeEditUnitId === activeEdit?.videoEditTargetId;
  const canShowEditAffordance =
    canEdit &&
    Boolean(pageEditMode?.editMode) &&
    Boolean(activeEdit?.videoEditTargetId);
  const sentimentData = useSentimentData({
    activeVideoId,
    videoDuration: duration,
    videoSectionId: activeVideoSectionId,
    videoSectionStats,
  });
  const sentimentStatus = sentiment.isPending
    ? 'pending'
    : sentiment.isSuccess
      ? 'success'
      : sentiment.isError
        ? 'error'
        : 'idle';
  const sentimentEffectContextRef = useRef({
    activeVideoIndex,
    addOptimisticSentiment: sentimentData.addOptimisticSentiment,
    currentTime,
    errorMessage: sentiment.error?.message,
    reset: sentiment.reset,
  });
  sentimentEffectContextRef.current = {
    activeVideoIndex,
    addOptimisticSentiment: sentimentData.addOptimisticSentiment,
    currentTime,
    errorMessage: sentiment.error?.message,
    reset: sentiment.reset,
  };

  useEffect(() => {
    const nextActiveVideoId =
      activeVideoId && videoIds.includes(activeVideoId)
        ? activeVideoId
        : (videoIds[0] ?? null);

    setActiveVideoId(nextActiveVideoId);
  }, [activeVideoId, videoIds]);

  useEffect(() => {
    if (!activeVideoId) {
      setAnnouncement('No videos are available for this component.');
      return;
    }

    setAnnouncement(`Video ${activeVideoIndex + 1} loaded.`);
  }, [activeVideoId, activeVideoIndex]);

  useEffect(() => {
    const {
      activeVideoIndex,
      addOptimisticSentiment,
      currentTime,
      errorMessage,
      reset,
    } = sentimentEffectContextRef.current;

    if (sentiment.isSuccess) {
      if (lastSubmittedSentimentTimeRef.current !== null) {
        addOptimisticSentiment(lastSubmittedSentimentTimeRef.current);
        lastSubmittedSentimentTimeRef.current = null;
      }

      setAnnouncement(
        `Confusion noted at ${formatPlaybackTime(currentTime)} in Video ${activeVideoIndex + 1}.`,
      );

      const timerId = window.setTimeout(() => {
        reset();
      }, 1800);

      return () => {
        window.clearTimeout(timerId);
      };
    }

    if (sentiment.isError) {
      setAnnouncement(
        errorMessage || 'Unable to record confusion feedback right now.',
      );
    }

    return undefined;
  }, [sentiment.isError, sentiment.isSuccess]);

  useEffect(() => {
    function handleFullscreenChange() {
      const isNowFullscreen =
        Boolean(sectionRef.current) &&
        sectionRef.current === document.fullscreenElement;
      setIsFullscreen(isNowFullscreen);

      if (!isNowFullscreen) {
        fullscreenTriggerRef.current?.focus();
      }
    }

    document.addEventListener('fullscreenchange', handleFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, []);

  useEffect(() => {
    if (!isPlaying) {
      setControlsVisible(true);
      return;
    }

    const activeElement = document.activeElement;

    if (sectionRef.current?.contains(activeElement)) {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      setControlsVisible(false);
    }, 3000);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [currentTime, isPlaying]);

  const canRecordConfusion = useMemo(
    () =>
      isStudentView &&
      Boolean(activeVideoId && activeVideoSectionId) &&
      hasStartedPlayback &&
      !sentiment.isPending,
    [
      activeVideoId,
      activeVideoSectionId,
      hasStartedPlayback,
      isStudentView,
      sentiment.isPending,
    ],
  );
  const confusionDisabledReason = !activeVideoSectionId
    ? "This video can't record confusion feedback yet."
    : !hasStartedPlayback
      ? 'Start playing the video first.'
      : undefined;

  function syncFromController(nextController: VideoPlayerHandle | null) {
    setController(nextController);

    if (!nextController) {
      return;
    }

    setCurrentTime(nextController.getCurrentTime());
    setDuration(nextController.getDuration());
    setIsMuted(nextController.isMuted());
    setPlaybackRate(clampPlaybackRate(nextController.getPlaybackRate()));
    setPlayerState(nextController.getPlayerState());
    setVolume(Math.round(nextController.getVolume()));
    setAnnouncement('Video player ready.');
  }

  function revealControls() {
    setControlsVisible(true);
  }

  function seekTo(seconds: number) {
    controller?.seek(seconds);
    setCurrentTime(seconds);
    revealControls();
  }

  function togglePlayPause() {
    if (!controller) {
      return;
    }

    if (isPlaying) {
      controller.pause();
      setPlayerState(PLAYER_STATES.PAUSED);
      setAnnouncement('Playback paused.');
    } else {
      controller.play();
      setPlayerState(PLAYER_STATES.PLAYING);
      setAnnouncement('Playback started.');
    }

    revealControls();
  }

  function toggleMute() {
    if (!controller) {
      return;
    }

    if (controller.isMuted()) {
      controller.unMute();
      setIsMuted(false);
      setAnnouncement('Sound on.');
    } else {
      controller.mute();
      setIsMuted(true);
      setAnnouncement('Sound muted.');
    }

    setVolume(Math.round(controller.getVolume()));
    revealControls();
  }

  function handleFullscreenToggle(trigger: HTMLElement | null) {
    fullscreenTriggerRef.current = trigger;

    if (!sectionRef.current) {
      return;
    }

    if (document.fullscreenElement === sectionRef.current) {
      void document.exitFullscreen?.();
      return;
    }

    void sectionRef.current.requestFullscreen?.();
  }

  function handlePlaybackRateChange(nextRate: PlaybackRate) {
    controller?.setPlaybackRate(nextRate);
    setPlaybackRate(nextRate);
    setAnnouncement(`Playback speed set to ${nextRate}x.`);
    revealControls();
  }

  function handleVolumeChange(nextVolume: number) {
    controller?.setVolume(nextVolume);
    if (nextVolume > 0) {
      controller?.unMute();
    }
    setVolume(nextVolume);
    setIsMuted(nextVolume === 0 ? true : (controller?.isMuted() ?? false));
    revealControls();
  }

  function handleVideoSelect(videoId: string) {
    setActiveVideoId(videoId);
    setPlayerError(null);
    setCurrentTime(0);
    revealControls();
  }

  function handleKeyDown(event: KeyboardEvent<HTMLElement>) {
    if (isVideoEditTarget) {
      return;
    }

    if (isFocusableControlsTarget(event.target)) {
      return;
    }

    if (event.key === ' ' || event.key === 'Spacebar') {
      event.preventDefault();
      togglePlayPause();
      return;
    }

    if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      event.preventDefault();
      const delta = event.shiftKey ? 10 : 5;
      const direction = event.key === 'ArrowRight' ? 1 : -1;
      seekTo(Math.min(Math.max(0, currentTime + direction * delta), duration || 0));
      return;
    }

    if (event.key.toLowerCase() === 'm') {
      event.preventDefault();
      toggleMute();
      return;
    }

    if (event.key.toLowerCase() === 'f') {
      event.preventDefault();
      handleFullscreenToggle(event.currentTarget);
    }
  }

  function handleControlBlur(event: FocusEvent<HTMLElement>) {
    const nextFocusedTarget = event.relatedTarget;

    if (nextFocusedTarget instanceof Node && event.currentTarget.contains(nextFocusedTarget)) {
      return;
    }

    if (isPlaying) {
      window.setTimeout(() => {
        const activeElement = document.activeElement;
        if (!sectionRef.current?.contains(activeElement)) {
          setControlsVisible(false);
        }
      }, 0);
    }
  }

  function handleRecordConfusion() {
    if (!activeVideoId || !activeVideoSectionId || !canRecordConfusion) {
      return;
    }

    const sentimentTime = controller?.getCurrentTime() ?? currentTime;
    lastSubmittedSentimentTimeRef.current = sentimentTime;

    sentiment.mutate({
      component: rawComponent,
      componentId,
      courseId,
      sentimentTime,
      videoId: activeVideoId,
      videoSectionId: activeVideoSectionId,
    });
  }

  return (
    <section
      aria-label="Video player"
      className={`${styles.section}${className ? ` ${className}` : ''}`}
      data-active-edit={isVideoEditTarget ? 'true' : 'false'}
      data-edit-mode={pageEditMode?.editMode ? 'true' : 'false'}
      onFocusCapture={revealControls}
      onKeyDown={handleKeyDown}
      onMouseMove={revealControls}
      onTouchStart={revealControls}
      ref={sectionRef}
      tabIndex={0}
    >
      <p aria-atomic="true" aria-live="polite" className={styles.liveRegion}>
        {announcement}
      </p>

      {canShowEditAffordance ? (
        <div className={styles.editAffordance}>
          {isVideoEditTarget ? (
            <>
              <button
                className={styles.editActionButton}
                onClick={() => {
                  activeEdit?.cancelActiveUnit();
                }}
                type="button"
              >
                Cancel
              </button>
              <button
                className={styles.editSaveButton}
                onClick={() => {
                  activeEdit?.commitActiveUnit();
                }}
                type="button"
              >
                Save
              </button>
            </>
          ) : hasVideos ? (
            <button
              aria-label="Edit"
              className={styles.editIconButton}
              onClick={() => {
                onBeforeEdit?.();
                activeEdit?.openVideoEdit();
              }}
              title="Edit"
              type="button"
            >
              <Pencil aria-hidden="true" size={15} strokeWidth={2} />
            </button>
          ) : (
            <button
              className={styles.editActionButton}
              onClick={() => {
                onBeforeEdit?.();
                activeEdit?.openVideoEdit();
              }}
              type="button"
            >
              <Plus aria-hidden="true" size={16} strokeWidth={2.4} />
              Add Video
            </button>
          )}
        </div>
      ) : null}

      {!isVideoEditTarget && videoIds.length > 1 ? (
        <VideoSelector
          activeVideoId={activeVideoId ?? videoIds[0]}
          onSelect={handleVideoSelect}
          videoIds={videoIds}
        />
      ) : null}

      <div className={styles.playerShell}>
        {isVideoEditTarget ? (
          <>
            <div className={styles.videoEditViewport}>
              <VideoSectionEditor
                onAddVideo={
                  activeEdit?.addVideoToDraft ??
                  (() => ({ success: false }))
                }
                onRemoveVideo={
                  activeEdit?.removeVideoFromDraft ?? (() => undefined)
                }
                onReorderVideo={
                  activeEdit?.reorderVideoDraft ?? (() => undefined)
                }
                variant="inline"
                videoIds={activeEdit?.videoDraftIds ?? videoIds}
              />
            </div>
            <div aria-hidden="true" className={styles.controlsPlaceholder} />
          </>
        ) : !hasVideos ? (
          <div className={styles.emptyVideoState}>
            <h2 className={styles.emptyVideoTitle}>No videos yet</h2>
            <p className={styles.emptyVideoMessage}>
              Use Add Video to add a YouTube video to this chapter.
            </p>
          </div>
        ) : (
          <>
            <YouTubePlayer
              onError={setPlayerError}
              onHandleChange={syncFromController}
              onSnapshotChange={(snapshot) => {
                setCurrentTime(snapshot.currentTime);
                setDuration(snapshot.duration);
                setIsMuted(snapshot.isMuted);
                setPlaybackRate(snapshot.playbackRate);
                setPlayerState(snapshot.playerState);
                setVolume(snapshot.volume);
              }}
              onStateChange={(nextPlayerState) => {
                setPlayerState(nextPlayerState);

                if (nextPlayerState === PLAYER_STATES.PLAYING) {
                  setAnnouncement('Playback started.');
                } else if (nextPlayerState === PLAYER_STATES.PAUSED) {
                  setAnnouncement('Playback paused.');
                } else if (nextPlayerState === PLAYER_STATES.ENDED) {
                  setAnnouncement('Playback ended.');
                  setControlsVisible(true);
                }
              }}
              videoId={activeVideoId}
            />

            {playerError ? (
              <div className={styles.errorCard} role="alert">
                <p>{playerError}</p>
              </div>
            ) : null}

            <div
              className={styles.controlsWrap}
              onBlurCapture={handleControlBlur}
              onFocusCapture={revealControls}
            >
              <ControlBar
                canRecordConfusion={canRecordConfusion}
                confusionDisabledReason={confusionDisabledReason}
                controlsVisible={controlsVisible}
                currentTime={currentTime}
                duration={duration}
                hasInstructorSentimentData={sentimentData.hasSentimentData}
                isFullscreen={isFullscreen}
                isMuted={isMuted}
                isPlaying={isPlaying}
                onConfused={handleRecordConfusion}
                onFullscreenToggle={handleFullscreenToggle}
                onPlaybackRateChange={handlePlaybackRateChange}
                onPlayPause={togglePlayPause}
                onSeek={seekTo}
                onToggleMute={toggleMute}
                onVolumeChange={handleVolumeChange}
                playbackRate={playbackRate}
                sentimentOverlay={sentimentData.overlay}
                sentimentStatus={sentimentStatus}
                showConfusedButton={isStudentView}
                showInstructorSentimentInfo={isInstructorView}
                unavailableReason={controller ? undefined : PLAYER_LOADING_REASON}
                volume={volume}
              />
            </div>
          </>
        )}
      </div>
    </section>
  );
}
